import { inject, Injectable } from '@angular/core';
import { DropdownData, ApiCallingV2Service, LookupsService, API_CONFIG } from '@zayan-erp/shared';
import { ProductCategoriesService, ProductCategory, ProductSubcategoriesService } from '@zayan-erp/home-building';
import { Observable, map, of, catchError } from 'rxjs';
import { OptionFilters, OptionFilterSelection } from '../models/deal-options.models';

interface ProductSubcategory {
  id?: string;
  description: string;
  code: string;
}

export interface CategoryWithMetadata {
  options: DropdownData[];
  metadata: Record<string, string>;
}

@Injectable({
  providedIn: 'root'
})
export class DealOptionFiltersService {
  readonly #lookupsService = inject(LookupsService);
  readonly #productCategoriesService = inject(ProductCategoriesService);
  readonly #productSubcategoriesService = inject(ProductSubcategoriesService);
  readonly #api = inject(ApiCallingV2Service);
  readonly #apiConfig = inject(API_CONFIG);
  baseUrl = this.#apiConfig.baseUrlV2;

  getRooms(): Observable<DropdownData[]> {
    return this.#lookupsService.readLocations().pipe(map(locations => locations ?? []));
  }

  getOptionFilters(
    modelId: string,
    elevationId: string,
    selection: OptionFilterSelection = {}
  ): Observable<OptionFilters> {
    const params = new URLSearchParams();
    if (selection.roomId) params.set('roomId', selection.roomId);
    if (selection.categoryId) params.set('categoryId', selection.categoryId);
    if (selection.subcategoryId) params.set('subcategoryId', selection.subcategoryId);
    if (selection.catalogChannel != null) params.set('catalogChannel', String(selection.catalogChannel));

    const query = params.toString();
    return this.#api
      .getData<OptionFilters>(
        `${this.baseUrl}/modelelevationproducts/models/${modelId}/elevations/${elevationId}/filters` +
          (query ? `?${query}` : ''),
        { showLoader: false, showMessage: false }
      )
      .pipe(
        catchError(() => of<OptionFilters>({ categories: [], subcategories: [], rooms: [], hasAnyOptions: false }))
      );
  }

  getCategories(): Observable<CategoryWithMetadata> {
    return this.#productCategoriesService.readProductCategories().pipe(
      map((categories: ProductCategory[] | null) => this.#mapCategories(categories)),
      catchError(() => {
        return of({ options: [], metadata: {} } as CategoryWithMetadata);
      })
    );
  }

  getSubcategories(categoryId: string, productListId: string, showLoader?: boolean): Observable<DropdownData[]> {
    if (!categoryId || !productListId) {
      return of([]);
    }

    return this.#productSubcategoriesService.readSubcategories(productListId, categoryId, showLoader).pipe(
      map(
        (subcategories: ProductSubcategory[] | null) =>
          subcategories?.map(sub => this.#mapToDropdown(sub.id ?? '', sub.description, sub.code)) ?? []
      ),
      catchError(() => {
        return of([]);
      })
    );
  }

  #mapToDropdown(id: string, firstLabel: string, secondLabel?: string): DropdownData {
    return { id, firstLabel, ...(secondLabel && { secondLabel }) };
  }

  #mapCategories(categories: ProductCategory[] | null): CategoryWithMetadata {
    const metadata: Record<string, string> = {};
    const options =
      categories?.map(category => {
        const id = category.id ?? '';
        metadata[id] = category.productListId;
        return this.#mapToDropdown(id, category.description, category.code);
      }) ?? [];

    return { options, metadata };
  }
  readCategoriesByModelElevation(modelId: string, elevationId: string): Observable<CategoryWithMetadata> {
    return this.#api
      .getData<ProductCategory[]>(
        `${this.baseUrl}/products/categories/by-model-elevation/models/${modelId}/elevations/${elevationId}?catalogChannel=0
`,
        { showLoader: true, showMessage: false }
      )
      .pipe(
        map(categories => this.#mapCategories(categories)),
        catchError(() => of({ options: [], metadata: {} } as CategoryWithMetadata))
      );
  }
}
